// frontend/src/components/admin/coupon/CouponForm.js - Fixed version with proper update handling

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    Ticket,
    ArrowLeft,
    Save,
    Percent,
    DollarSign,
    Calendar,
    AlertCircle,
    Tag,
    RefreshCw,
    Info
} from 'lucide-react';

const CouponForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        type: 'percentage',
        value: '',
        minOrderValue: '',
        maxDiscount: '',
        validFrom: new Date().toISOString().split('T')[0],
        validUntil: '',
        isActive: true,
        description: ''
    });
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(isEditMode);

    useEffect(() => {
        if (isEditMode) {
            fetchCouponDetails();
        }
    }, [id]);

    const fetchCouponDetails = async () => {
        try {
            setFetchLoading(true);
            const { data } = await axios.get(`/api/v1/admin/coupon/${id}`, {
                withCredentials: true
            });
            if (data.success) {
                const coupon = data.coupon;
                setFormData({
                    name: coupon.name,
                    code: coupon.code,
                    type: coupon.type,
                    value: coupon.value,
                    minOrderValue: coupon.minOrderValue,
                    maxDiscount: coupon.maxDiscount,
                    validFrom: new Date(coupon.validFrom).toISOString().split('T')[0],
                    validUntil: new Date(coupon.validUntil).toISOString().split('T')[0],
                    isActive: coupon.isActive,
                    description: coupon.description || ''
                });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching coupon details');
            navigate('/admin/coupons');
        } finally {
            setFetchLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const validateForm = () => {
        const errors = [];
        if (!formData.name.trim()) errors.push('Coupon name is required');
        if (!formData.code.trim()) errors.push('Coupon code is required');
        
        if (!formData.value || isNaN(formData.value) || Number(formData.value) <= 0) {
            errors.push('Please enter a valid coupon value');
        }
        
        if (formData.type === 'percentage' && Number(formData.value) > 100) {
            errors.push('Percentage value cannot exceed 100%');
        }
        
        if (!formData.minOrderValue || isNaN(formData.minOrderValue) || Number(formData.minOrderValue) < 0) {
            errors.push('Please enter a valid minimum order value');
        }
        
        if (!formData.maxDiscount || isNaN(formData.maxDiscount) || Number(formData.maxDiscount) <= 0) {
            errors.push('Please enter a valid maximum discount amount');
        }
        
        if (!formData.validUntil) errors.push('Validity end date is required');
        
        if (formData.validUntil && new Date(formData.validUntil) <= new Date(formData.validFrom)) {
            errors.push('End date must be after start date');
        }
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const errors = validateForm();
        if (errors.length > 0) {
            errors.forEach(error => toast.error(error));
            return;
        }
        
        try {
            setLoading(true);
            
            // Ensure coupon code is in uppercase
            const submissionData = {
                ...formData,
                code: formData.code.toUpperCase()
            };
            
            let response;
            
            if (isEditMode) {
                // For edit, make sure to include all necessary fields
                response = await axios.put(`/api/v1/admin/coupon/${id}`, submissionData, {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true
                });
            } else {
                response = await axios.post('/api/v1/admin/coupon/new', submissionData, {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true
                });
            }
            
            if (response.data.success) {
                toast.success(isEditMode ? 'Coupon updated successfully' : 'Coupon created successfully');
                
                // Force a slight delay to ensure the server has processed the update
                setTimeout(() => {
                    // Use replace instead of push to prevent going back to the form
                    navigate('/admin/coupons', { replace: true });
                }, 500);
            }
        } catch (error) {
            console.error('Error submitting coupon:', error);
            toast.error(error.response?.data?.message || `Error ${isEditMode ? 'updating' : 'creating'} coupon`);
        } finally {
            setLoading(false);
        }
    };

    if (fetchLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <RefreshCw className="h-8 w-8 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-3xl mx-auto">
                <div className="flex items-center mb-6">
                    <button
                        onClick={() => navigate('/admin/coupons')}
                        className="flex items-center text-gray-600 hover:text-[#dc2626] transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5 mr-1" />
                        <span>Back to Coupons</span>
                    </button>
                </div>
                
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <h1 className="text-xl font-bold text-gray-900 mb-6 font-['Crimson_Text']">
                        <Ticket className="inline h-5 w-5 mr-2 text-[#dc2626]" />
                        {isEditMode ? 'Edit Coupon' : 'Create New Coupon'}
                    </h1>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Coupon Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Coupon Name*
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter coupon name"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                                    required
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    A descriptive name for internal reference
                                </p>
                            </div>
                            
                            {/* Coupon Code */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Coupon Code*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Tag className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        placeholder="Enter coupon code"
                                        className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent uppercase ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        required
                                        readOnly={isEditMode}
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    {isEditMode 
                                        ? 'Coupon code cannot be edited once created' 
                                        : 'Will be converted to uppercase automatically'}
                                </p>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Coupon Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Discount Type*
                                </label>
                                <div className="relative">
                                    <select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                                        required
                                    >
                                        <option value="percentage">Percentage (%)</option>
                                        <option value="amount">Fixed Amount (₹)</option>
                                    </select>
                                </div>
                            </div>
                            
                            {/* Discount Value */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Discount Value*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        {formData.type === 'percentage' ? (
                                            <Percent className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <DollarSign className="h-5 w-5 text-gray-400" />
                                        )}
                                    </div>
                                    <input
                                        type="number"
                                        name="value"
                                        value={formData.value}
                                        onChange={handleInputChange}
                                        placeholder={formData.type === 'percentage' ? "Enter percentage value" : "Enter amount value"}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                                        required
                                        min="1"
                                        max={formData.type === 'percentage' ? "100" : undefined}
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    {formData.type === 'percentage' ? 'Percentage discount (max 100%)' : 'Fixed amount discount in rupees'}
                                </p>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Minimum Order Value */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Minimum Order Value*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <DollarSign className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="number"
                                        name="minOrderValue"
                                        value={formData.minOrderValue}
                                        onChange={handleInputChange}
                                        placeholder="Enter minimum order value"
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                                        required
                                        min="0"
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Minimum order amount required to use this coupon
                                </p>
                            </div>
                            
                            {/* Maximum Discount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Maximum Discount*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <DollarSign className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="number"
                                        name="maxDiscount"
                                        value={formData.maxDiscount}
                                        onChange={handleInputChange}
                                        placeholder="Enter maximum discount amount"
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                                        required
                                        min="1"
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Maximum discount amount in rupees (applies to percentage discounts)
                                </p>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Valid From */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Valid From*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Calendar className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="date"
                                        name="validFrom"
                                        value={formData.validFrom}
                                        onChange={handleInputChange}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                                        required
                                    />
                                </div>
                            </div>
                            
                            {/* Valid Until */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Valid Until*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Calendar className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="date"
                                        name="validUntil"
                                        value={formData.validUntil}
                                        onChange={handleInputChange}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                                        required
                                        min={formData.validFrom}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Active Status */}
                        <div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    id="isActive"
                                    checked={formData.isActive}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-[#dc2626] focus:ring-[#dc2626] border-gray-300 rounded"
                                />
                                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                                    Active
                                </label>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                If unchecked, the coupon will not be available for use
                            </p>
                        </div>
                        
                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Enter coupon description (optional)"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                                rows="3"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Optional description for internal reference
                            </p>
                        </div>
                        
                        {/* Example of how the discount would be applied */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-2">
                                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div>
                                    <h3 className="text-sm font-medium text-blue-800">Example of how this coupon would work:</h3>
                                    <div className="mt-2 text-sm text-blue-700 space-y-2">
                                        <p>
                                            <strong>Minimum order value:</strong> ₹{formData.minOrderValue || 0}
                                        </p>
                                        <p>
                                            <strong>Discount:</strong> {formData.type === 'percentage' ? `${formData.value || 0}% of the order value` : `₹${formData.value || 0} fixed amount`}
                                        </p>
                                        <p>
                                            <strong>Maximum discount cap:</strong> ₹{formData.maxDiscount || 0}
                                        </p>
                                        
                                        {formData.type === 'percentage' && formData.value && formData.maxDiscount && (
                                            <p>
                                                <strong>Example calculation:</strong> For an order of ₹{Math.max(1000, formData.minOrderValue * 2)},
                                                the {formData.value}% discount would be
                                                ₹{(Math.max(1000, formData.minOrderValue * 2) * formData.value / 100).toFixed(2)},
                                                {(Math.max(1000, formData.minOrderValue * 2) * formData.value / 100) > formData.maxDiscount
                                                    ? ` but it will be capped at ₹${formData.maxDiscount}`
                                                    : ` which is within the maximum discount limit`}.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/coupons')}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none mr-2"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 focus:outline-none flex items-center"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                                        {isEditMode ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4 mr-2" />
                                        {isEditMode ? 'Update Coupon' : 'Create Coupon'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CouponForm;